import { m } from 'framer-motion';
import { useState, useCallback, useEffect } from 'react';
// @mui
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// _mock
import { _notifications } from 'src/_mock';
// components
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
// import { useDashboardContext, useRealTimeContext } from 'src/context/hooks';
import { varHover } from 'src/components/animate';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { sortBy } from 'lodash';
//
import { ILog } from 'src/types/logs';
import { useAuthContext } from 'src/auth/hooks';
import useFetcher, { endpoints } from 'src/utils/axios';
import { IUser } from 'src/types/user';
import { useDashboardContext } from 'src/context/hooks';
import NotificationItem from './notification-item';

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const drawer = useBoolean();
  const smUp = useResponsive('up', 'sm');
  const { db } = useAuthContext();
  const { currentOrg } = useDashboardContext();
  const { fetchData } = useFetcher();
  const [user, setUser] = useState<IUser[]>();
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    if (currentOrg) {
      fetchData([endpoints.users.get, { params: { org_id: currentOrg.org_id } }]).then(
        (response: IUser[]) => setUser(response)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg]);

  function arraysEqual(arr1: any, arr2: any) {
    return JSON.stringify(arr1) === JSON.stringify(arr2);
  }
  const [notifications, setNotifications] = useState<ILog[]>();

  const displayedNotifications = showAll ? notifications?.slice(1) : notifications?.slice(1, 11);

  useEffect(() => {
    if (currentOrg) {
      const q = query(collection(db, 'notification'), where('org_id', '==', currentOrg.org_id));

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const notes: any = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();

          data.createdDate = new Date(data.createdDate);
          notes.push(data);
        });
        if (!arraysEqual(notes, notifications)) {
          notes.forEach((note: any) => {});

          setNotifications(sortBy(notes, (item) => item.createdDate.getTime()).reverse());
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, currentOrg]);

  const totalUnRead = notifications?.filter((item: any) => item.isUnRead === true).length;

  const handleMarkAllAsRead = () => {
    setNotifications(
      notifications?.map((notification: any) => ({
        ...notification,
        isUnRead: false,
      }))
    );
  };

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!!totalUnRead && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderList = (
    <Scrollbar>
      <List disablePadding>
        {displayedNotifications?.map((notification: any) => (
          <NotificationItem key={notification.id} notification={notification} user={user} />
        ))}
      </List>
    </Scrollbar>
  );  

  const renderButton = () => {
    if (showAll) {
      return (
        <Button fullWidth size="large" onClick={() => setShowAll(false)}>
          View Less
        </Button>
      );
    }
    return (
      <Button fullWidth size="large" onClick={() => setShowAll(true)}>
        View All
      </Button>
    );
  }

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={drawer.onTrue}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 1, maxWidth: 420 },
        }}
      >
        {renderHead}

        <Divider />

        {renderList}

        <Box sx={{ p: 1 }}>
          {renderButton()}
        </Box>
      </Drawer>
    </>
  );
}
